import { useEffect, useState } from "react";
import {
  Wifi, Loader2, CheckCircle2, XCircle, Smartphone, Zap, Clock,
  ChevronDown, Ticket, Copy, ArrowRight, Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { devWarn } from "@/lib/logger";
import { KES, formatDuration, formatBandwidth, formatCountdown } from "@/lib/format";
import { getHotspotParams } from "@/lib/hotspotParams";
import { formatMacInput } from "@/lib/mac";

// lucide-react deliberately excludes brand/logo glyphs, so the WhatsApp mark
// is a small hand-drawn SVG instead of a new icon-library dependency.
function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.868-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.67-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.745.456 3.448 1.323 4.95L2 22l5.272-1.382a9.86 9.86 0 0 0 4.768 1.213h.004c5.46 0 9.9-4.44 9.9-9.9 0-2.646-1.03-5.13-2.901-7.002A9.83 9.83 0 0 0 12.04 2zm0 18.12h-.003a8.2 8.2 0 0 1-4.18-1.144l-.3-.178-3.129.82.835-3.05-.195-.313a8.2 8.2 0 0 1-1.256-4.365c0-4.534 3.69-8.223 8.23-8.223a8.17 8.17 0 0 1 5.82 2.412 8.17 8.17 0 0 1 2.408 5.818c0 4.534-3.69 8.223-8.23 8.223z" />
    </svg>
  );
}

// Per-device packages: price-per-device × count, 25% off at 10+ devices, capped at 20.
const DEVICE_PRICE_MAX = 20;
const DEVICE_PRICE_DISCOUNT_THRESHOLD = 10;
const DEVICE_PRICE_DISCOUNT_RATE = 0.25;
function computeTotal(pkg, count) {
  if (!pkg?.per_device_pricing) return pkg?.price_kes || 0;
  const n = Math.min(DEVICE_PRICE_MAX, Math.max(1, count));
  let total = pkg.price_kes * n;
  if (n >= DEVICE_PRICE_DISCOUNT_THRESHOLD) total *= 1 - DEVICE_PRICE_DISCOUNT_RATE;
  return Math.round(total * 100) / 100;
}

// Steps: 1=browse packages, 2=phone entry, 3=waiting/paying, 4=connected, 5=failed
export default function CaptivePortal() {
  const [step, setStep] = useState(1);
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [deviceCount, setDeviceCount] = useState(1);
  const [buyForOtherDevice, setBuyForOtherDevice] = useState(false);
  const [targetMac, setTargetMac] = useState("");
  const [sites, setSites] = useState([]);
  const [targetSiteId, setTargetSiteId] = useState("");
  const [phone, setPhone] = useState("");
  const [transactionId, setTransactionId] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [busy, setBusy] = useState(false);
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [receiptConnectOpen, setReceiptConnectOpen] = useState(false);
  const [receiptConnectPrefillMac, setReceiptConnectPrefillMac] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portal/packages");
        setPackages(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
    // Only matters for "buy for a different device" (no client IP there to
    // match a router's subnet against), fetched up front so it's ready the
    // moment that checkbox is ticked.
    (async () => {
      try {
        const { data } = await api.get("/portal/sites");
        setSites(data);
        if (data.length === 1) setTargetSiteId(data[0].id);
      } catch { /* non-fatal -- single-site deployments don't need this at all */ }
    })();
    // Capture the router's mac/ip as early as possible in case the WebView
    // drops the query string before the user reaches the payment step.
    const params = new URLSearchParams(window.location.search);
    const mac = params.get("mac");
    const ip = params.get("ip");
    if (mac || ip) {
      try { sessionStorage.setItem("nhp_hotspot_params", JSON.stringify({ mac, ip })); } catch { /* storage blocked — non-fatal */ }
    }
  }, []);

  useEffect(() => {
    if (!transactionId || step !== 3) return;
    let alive = true;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/portal/transaction/${transactionId}`);
        if (!alive) return;
        setTransaction(data);
        if (data.status === "success" && data.subscription) {
          setSubscription(data.subscription);
          if (buyForOtherDevice) {
            // Bought for a MAC that isn't this browser -- never show the
            // "you're online" countdown here, that's only true for the
            // target device, not this one.
            setStep(7);
          } else {
            // This device normally auto-connects on payment (see
            // payment.service.js). mac_address stays unset on the rare occasions
            // it couldn't (no device info was captured, or this mac is already
            // registered to a different account) -- then it has to be claimed
            // with the M-Pesa receipt code below instead.
            setStep(data.subscription.mac_address ? 4 : 6);
          }
        } else if (data.status === "failed") {
          setStep(5);
        }
      } catch (e) {
        // Poll error is normally transient (backend hot-reload, network blip).
        devWarn("[portal/tx-poll]", e?.message);
      }
    }, 1500);
    return () => { alive = false; clearInterval(iv); };
  }, [transactionId, step]);

  useEffect(() => {
    if (!subscription) return;
    setRemainingMs(Math.max(0, new Date(subscription.expires_at).getTime() - Date.now()));
    const iv = setInterval(() => {
      const ms = new Date(subscription.expires_at).getTime() - Date.now();
      setRemainingMs(Math.max(0, ms));
    }, 500);
    return () => clearInterval(iv);
  }, [subscription]);

  // The router's captive-portal redirect carries the client's mac/ip. We hand
  // these to the backend with the STK push so it can grant hotspot access
  // itself (via an ip-binding bypass over the RouterOS API) once payment
  // succeeds — no login form submission from this page is needed.
  const startPayment = async () => {
    if (!selected) return;
    if (!phone || phone.replace(/\D/g, "").length < 9) {
      toast.error("Enter a valid Kenyan phone number");
      return;
    }
    if (buyForOtherDevice && targetMac.length !== 17) {
      toast.error("Enter the other device's full MAC address");
      return;
    }
    if (buyForOtherDevice && sites.length > 1 && !targetSiteId) {
      toast.error("Choose which site that device is at");
      return;
    }
    setBusy(true);
    try {
      const payload = { phone, package_id: selected.id };
      if (buyForOtherDevice) {
        // A device that may have never joined this WiFi at all -- the
        // backend's RouterOS bypass binds by MAC alone, no IP/DHCP lease
        // needed, so it'll get online the moment it does connect. There's no
        // client IP here to tell the backend which site's router to use
        // (unlike the normal purchase path), so the site has to be explicit.
        payload.mac_address = targetMac.trim();
        if (targetSiteId) payload.site_id = targetSiteId;
      } else {
        const hotspotParams = getHotspotParams();
        if (hotspotParams.mac) payload.mac_address = hotspotParams.mac;
        if (hotspotParams.ip) payload.ip_address = hotspotParams.ip;
      }
      if (selected.per_device_pricing) payload.device_count = deviceCount;
      const { data } = await api.post("/portal/stk-push", payload);
      setTransactionId(data.transaction_id);
      setStep(3);
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setStep(1); setSelected(null); setPhone(""); setTransactionId(null);
    setTransaction(null); setSubscription(null); setDeviceCount(1);
    setBuyForOtherDevice(false); setTargetMac(""); setReceiptConnectPrefillMac("");
    setTargetSiteId(sites.length === 1 ? sites[0].id : "");
  };

  const { mac: hotspotMac, ip: hotspotIp } = getHotspotParams();
  const accountLinkQs = new URLSearchParams({ ...(hotspotMac ? { mac: hotspotMac } : {}), ...(hotspotIp ? { ip: hotspotIp } : {}) }).toString();
  const accountHref = accountLinkQs ? `/customer/login?${accountLinkQs}` : "/customer/login";

  return (
    <div
      className="min-h-screen bg-[#F7F7F5] text-[#1A1C20] relative"
      style={{ backgroundImage: "radial-gradient(1100px circle at 12% -10%, rgba(67,176,42,0.05), transparent 55%)" }}
    >

      <header className="relative z-10 max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-y-2">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 grid place-items-center bg-[#43B02A] text-white rounded-full">
            <Wifi className="h-5 w-5" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-black text-lg leading-none tracking-tight">NetOnLain</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A] mt-1">Fast · Fair · Kenyan</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <a
            href={accountHref}
            data-testid="portal-account-link"
            className="group flex items-center gap-1.5 rounded-full border border-[#1A1C20]/15 bg-white px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-[#1A1C20] transition-colors hover:border-[#43B02A] hover:text-[#43B02A]"
          >
            My Account
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
          </a>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 pb-8">
        {step === 1 && (
          <PackageSelection
            packages={packages}
            onSelect={(p) => { setSelected(p); setDeviceCount(1); setStep(2); }}
            onOpenVoucher={() => setVoucherOpen(true)}
            onOpenReceiptConnect={() => setReceiptConnectOpen(true)}
          />
        )}
        {step === 2 && selected && (
          <PhoneEntry
            pkg={selected}
            deviceCount={deviceCount} setDeviceCount={setDeviceCount}
            phone={phone} setPhone={setPhone}
            buyForOtherDevice={buyForOtherDevice} setBuyForOtherDevice={setBuyForOtherDevice}
            targetMac={targetMac} setTargetMac={setTargetMac}
            sites={sites} targetSiteId={targetSiteId} setTargetSiteId={setTargetSiteId}
            busy={busy}
            onBack={() => setStep(1)}
            onPay={startPayment}
          />
        )}
        {step === 3 && (<Waiting phone={phone} amount={computeTotal(selected, deviceCount)} onCancel={restart} />)}
        {step === 4 && subscription && (
          <Connected
            sub={subscription} remainingMs={remainingMs} onDone={restart} receipt={transaction?.mpesa_receipt}
            onConnectAnotherDevice={() => { setReceiptConnectPrefillMac(""); setReceiptConnectOpen(true); }}
          />
        )}
        {step === 5 && (<Failed onRetry={() => setStep(2)} />)}
        {step === 6 && subscription && (
          <DeviceClaimPending
            sub={subscription} receipt={transaction?.mpesa_receipt}
            onConnectThisDevice={() => { setReceiptConnectPrefillMac(""); setReceiptConnectOpen(true); }}
            onDone={restart}
          />
        )}
        {step === 7 && subscription && (
          <BoughtForOtherDevice
            sub={subscription} mac={targetMac} onDone={restart}
            onRetryConnect={() => { setReceiptConnectPrefillMac(targetMac); setReceiptConnectOpen(true); }}
          />
        )}
      </main>

      <div className="relative z-10 max-w-6xl mx-auto px-6 pb-4 flex flex-wrap items-center justify-center gap-3">
        <a
          href="tel:+254728802160"
          data-testid="contact-phone-link"
          className="group flex items-center gap-2 rounded-full border-2 border-[#E1E4E8] bg-white px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.15em] text-[#1A1C20] transition-colors hover:border-[#43B02A] hover:text-[#43B02A]"
        >
          <Phone className="h-3.5 w-3.5" />
          0728 802 160
        </a>
        <div
          data-testid="contact-whatsapp-text"
          className="flex items-center gap-2 rounded-full border-2 border-[#E1E4E8] bg-white px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.15em] text-[#1A1C20]"
        >
          <WhatsAppIcon className="h-3.5 w-3.5 text-[#25D366]" />
          +254 735 219899
        </div>
      </div>

      <footer className="relative z-10 py-4 text-center text-xs font-mono uppercase tracking-[0.2em] text-[#57606A]">
        © 2026 NetOnLain Ltd · Nairobi, Kenya
      </footer>

      <VoucherDialog
        open={voucherOpen}
        onOpenChange={setVoucherOpen}
        onRedeemed={(sub) => {
          setVoucherOpen(false);
          setSubscription(sub);
          setStep(4);
        }}
      />

      <ReceiptConnectDialog
        open={receiptConnectOpen}
        onOpenChange={setReceiptConnectOpen}
        prefillReceipt={step === 6 || step === 7 ? transaction?.mpesa_receipt : ""}
        prefillMac={receiptConnectPrefillMac}
        onConnected={(sub) => {
          setReceiptConnectOpen(false);
          setSubscription(sub);
          setStep(4);
        }}
      />
    </div>
  );
}

/* ---------------- Step 1: Package selection ---------------- */
const TAB_META = [
  { id: "daily",   label: "Daily",   hint: "Up to 24 hours" },
  { id: "weekly",  label: "Weekly",  hint: "2–7 days" },
  { id: "monthly", label: "Monthly", hint: "30+ days" },
];
function categorize(pkg) {
  if (pkg.duration_minutes <= 1440) return "daily";
  if (pkg.duration_minutes <= 10080) return "weekly";
  return "monthly";
}

function PackageSelection({ packages, onSelect, onOpenVoucher, onOpenReceiptConnect }) {
  const [expandedId, setExpandedId] = useState(null);
  const toggle = (id) => setExpandedId((current) => (current === id ? null : id));
  const grouped = { daily: [], weekly: [], monthly: [] };
  for (const p of packages) grouped[categorize(p)].push(p);
  const firstWithData = TAB_META.find((t) => grouped[t.id].length > 0)?.id || "daily";
  const [tab, setTab] = useState(firstWithData);

  const renderGrid = (list) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
      {list.length === 0 && (
        <div className="col-span-full text-center py-12 text-[#57606A] font-mono text-sm">No packages in this category yet.</div>
      )}
      {list.map((p) => {
        const isOpen = expandedId === p.id;
        return (
          <div key={p.id} data-testid={`package-${p.id}`}
               className={`border bg-white transition-[border-color] duration-200 ${isOpen ? "border-[#43B02A]" : "border-[#E1E4E8] hover:border-[#43B02A]"}`}>
            <button type="button" onClick={() => toggle(p.id)} aria-expanded={isOpen}
                    data-testid={`package-toggle-${p.id}`}
                    className="w-full text-left p-5 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-[#43B02A] shrink-0" strokeWidth={2} />
                  <div className="font-heading font-bold text-lg truncate">{p.name}</div>
                </div>
                <div className="font-mono text-xs text-[#57606A] mt-1">
                  {formatDuration(p.duration_minutes)} · ↓ {formatBandwidth(p.download_kbps)}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <span className="font-mono font-black text-2xl text-[#1A1C20] tabular-nums">{KES(p.price_kes)}</span>
                  {p.per_device_pricing && <div className="text-[10px] text-[#57606A] font-mono uppercase tracking-[0.15em]">per device</div>}
                </div>
                <ChevronDown className={`h-5 w-5 text-[#57606A] transition-transform duration-200 ${isOpen ? "rotate-180 text-[#43B02A]" : ""}`} strokeWidth={2} />
              </div>
            </button>
            {isOpen && (
              <div className="px-5 pb-5 border-t border-[#E1E4E8]">
                <p className="mt-4 text-sm text-[#57606A] leading-relaxed">{p.description || "Instant hotspot access with full-speed bandwidth."}</p>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <div className="border border-[#E1E4E8] p-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Duration</div>
                    <div className="font-mono font-bold text-[#43B02A] mt-1 text-sm">{formatDuration(p.duration_minutes)}</div>
                  </div>
                  <div className="border border-[#E1E4E8] p-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Download</div>
                    <div className="font-mono font-bold text-[#1A1C20] mt-1 text-sm">{formatBandwidth(p.download_kbps)}</div>
                  </div>
                  <div className="border border-[#E1E4E8] p-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Upload</div>
                    <div className="font-mono font-bold text-[#1A1C20] mt-1 text-sm">{formatBandwidth(p.upload_kbps)}</div>
                  </div>
                </div>
                <Button type="button" data-testid={`package-continue-${p.id}`} onClick={() => onSelect(p)}
                        className="mt-5 w-full h-11 rounded-full bg-[#43B02A] hover:bg-[#368F22] text-white font-bold tracking-wide">
                  CONTINUE WITH {p.name.toUpperCase()}
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div data-testid="step-packages">
      <div className="max-w-2xl">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#43B02A] mb-3">// WELCOME TO NETONLAIN HOTSPOT</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl leading-[1.02] tracking-tight">Pick a package. Pay with M-Pesa. Get online.</h1>
        <p className="mt-4 text-[#57606A] text-base sm:text-lg leading-relaxed">Tap a package to see full details, then continue to pay.</p>
      </div>

      {packages.length === 0 ? (
        <div className="mt-10 text-center py-16 text-[#57606A]"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />Loading available packages...</div>
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="mt-10">
          <TabsList className="bg-white border border-[#E1E4E8] rounded-full h-12 p-1 gap-1 w-fit">
            {TAB_META.map((t) => {
              const count = grouped[t.id].length;
              return (
                <TabsTrigger key={t.id} value={t.id} data-testid={`tab-${t.id}`}
                             className="rounded-full px-5 h-10 data-[state=active]:bg-[#43B02A] data-[state=active]:text-white data-[state=active]:shadow-none text-[#57606A] font-mono uppercase tracking-[0.15em] text-xs">
                  {t.label}<span className="ml-2 font-mono text-[10px] opacity-70">({count})</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">{TAB_META.find((t) => t.id === tab)?.hint}</div>
          {TAB_META.map((t) => (<TabsContent key={t.id} value={t.id} className="mt-6">{renderGrid(grouped[t.id])}</TabsContent>))}
        </Tabs>
      )}

      {/* Voucher redemption entry */}
      <div className="mt-8 border-t border-[#E1E4E8] pt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between max-w-3xl">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">// GOT A VOUCHER CODE?</div>
          <div className="mt-1 text-sm text-[#57606A]">Have a prepaid voucher code from an agent or shop? Redeem it here instead of paying by M-Pesa.</div>
        </div>
        <Button
          variant="outline"
          data-testid="open-voucher-btn"
          onClick={onOpenVoucher}
          className="rounded-full h-11 border-[#1A1C20] text-[#1A1C20] hover:bg-[#1A1C20] hover:text-white font-bold w-full sm:w-auto shrink-0"
        >
          <Ticket className="h-4 w-4 mr-2" /> REDEEM VOUCHER
        </Button>
      </div>

      {/* Quick-reconnect using an existing M-Pesa receipt */}
      <div className="mt-4 border-t border-[#E1E4E8] pt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between max-w-3xl">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">// HAVE AN M-PESA CODE?</div>
          <div className="mt-1 text-sm text-[#57606A]">Enter the code from a payment — yours, or one shared with you — to bring this device online.</div>
        </div>
        <Button
          variant="outline"
          data-testid="open-receipt-connect-btn"
          onClick={onOpenReceiptConnect}
          className="rounded-full h-11 border-[#1A1C20] text-[#1A1C20] hover:bg-[#1A1C20] hover:text-white font-bold w-full sm:w-auto shrink-0"
        >
          <Zap className="h-4 w-4 mr-2" /> USE M-PESA CODE
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Step 2: Phone entry ---------------- */
function PhoneEntry({
  pkg, deviceCount, setDeviceCount, phone, setPhone,
  buyForOtherDevice, setBuyForOtherDevice, targetMac, setTargetMac,
  sites, targetSiteId, setTargetSiteId,
  onPay, onBack, busy,
}) {
  const needsSite = buyForOtherDevice && sites.length > 1;
  const total = computeTotal(pkg, deviceCount);
  const discounted = pkg.per_device_pricing && deviceCount >= DEVICE_PRICE_DISCOUNT_THRESHOLD;
  return (
    <div data-testid="step-phone" className="max-w-lg mx-auto pt-8">
      <button onClick={onBack} data-testid="back-btn" className="text-sm text-[#57606A] hover:text-[#43B02A] mb-6 font-mono">← Choose different package</button>

      <div className="border border-[#E1E4E8] bg-white p-8">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#43B02A]">// STEP 2 OF 3</div>
        <h2 className="font-heading font-black text-3xl mt-2">Enter M-Pesa number</h2>

        <div className="mt-6 border border-[#E1E4E8] bg-[#F7F7F5] p-4 flex items-center justify-between">
          <div>
            <div className="font-heading font-bold">{pkg.name}</div>
            <div className="text-xs text-[#57606A] font-mono">{formatDuration(pkg.duration_minutes)} · ↓ {formatBandwidth(pkg.download_kbps)}</div>
          </div>
          <div className="text-right">
            <div className="font-mono font-black text-2xl text-[#43B02A]">{KES(total)}</div>
            {pkg.per_device_pricing && <div className="text-[11px] text-[#57606A] font-mono">{KES(pkg.price_kes)} × {deviceCount}{discounted ? " − 25%" : ""}</div>}
          </div>
        </div>

        {pkg.per_device_pricing && (
          <div className="mt-6 border border-[#E1E4E8] bg-[#F7F7F5] p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-bold">How many devices?</div>
                <div className="text-xs text-[#57606A]">
                  10+ devices get 25% off the total. Max 20. Extra devices connect by entering
                  your M-Pesa receipt code on the captive portal after paying.
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button type="button" data-testid="device-count-decrement"
                        onClick={() => setDeviceCount((n) => Math.max(1, n - 1))}
                        className="h-9 w-9 rounded-full border border-[#1A1C20] font-bold text-lg leading-none grid place-items-center hover:bg-[#1A1C20] hover:text-white">−</button>
                <span data-testid="device-count-value" className="font-mono font-black text-xl w-8 text-center tabular-nums">{deviceCount}</span>
                <button type="button" data-testid="device-count-increment"
                        onClick={() => setDeviceCount((n) => Math.min(DEVICE_PRICE_MAX, n + 1))}
                        className="h-9 w-9 rounded-full border border-[#1A1C20] font-bold text-lg leading-none grid place-items-center hover:bg-[#1A1C20] hover:text-white">+</button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 border border-[#E1E4E8] bg-[#F7F7F5] p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox" data-testid="buy-other-device-toggle"
              checked={buyForOtherDevice}
              onChange={(e) => setBuyForOtherDevice(e.target.checked)}
              className="mt-1 h-4 w-4 accent-[#43B02A]"
            />
            <span>
              <span className="text-sm font-bold block">Buying for a different device?</span>
              <span className="text-xs text-[#57606A]">
                For a smart TV, printer, or anything else that can&apos;t open this page itself —
                enter its MAC address below and it&apos;ll get online automatically once it joins this WiFi.
              </span>
            </span>
          </label>
          {buyForOtherDevice && (
            <div className="mt-3">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Device MAC Address</label>
              <Input
                data-testid="target-mac-input" value={targetMac}
                onChange={(e) => setTargetMac(formatMacInput(e.target.value))}
                placeholder="e.g. AA:BB:CC:DD:EE:FF"
                type="text" inputMode="text" autoComplete="off" autoCapitalize="characters"
                className="mt-2 h-12 font-mono uppercase tracking-wider rounded-full border-[#E1E4E8] focus-visible:ring-[#43B02A]"
              />
              <div className="text-xs text-[#57606A] mt-2">Usually found in that device&apos;s network or WiFi settings.</div>
              {needsSite && (
                <div className="mt-3">
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Which site is this device at?</label>
                  <select
                    data-testid="target-site-select" value={targetSiteId}
                    onChange={(e) => setTargetSiteId(e.target.value)}
                    className="mt-2 h-12 w-full rounded-full border border-[#E1E4E8] bg-white px-4 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#43B02A]"
                  >
                    <option value="">Select a site…</option>
                    {sites.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}{s.location ? ` — ${s.location}` : ""}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6">
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Safaricom Phone Number</label>
          <div className="mt-2 relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 font-mono font-bold text-[#43B02A]">+254</div>
            <Input
              data-testid="phone-input" type="tel" inputMode="tel" placeholder="7XX XXX XXX"
              value={phone} onChange={(e) => setPhone(e.target.value)}
              className="pl-16 h-14 text-lg font-mono rounded-full border-[#E1E4E8] focus-visible:ring-[#43B02A]"
            />
          </div>
          <div className="text-xs text-[#57606A] mt-2 font-mono">You&apos;ll receive an M-Pesa PIN prompt on this number.</div>
        </div>

        <Button data-testid="pay-btn" onClick={onPay} disabled={busy || (buyForOtherDevice && targetMac.length !== 17) || (needsSite && !targetSiteId)}
                className="mt-6 w-full h-14 rounded-full bg-[#43B02A] hover:bg-[#368F22] text-white font-bold text-base tracking-wide">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>PAY {KES(total)} VIA M-PESA</>}
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Step 3: Waiting for callback ---------------- */
function Waiting({ phone, amount, onCancel }) {
  return (
    <div data-testid="step-waiting" className="max-w-lg mx-auto pt-8">
      <div className="border border-[#E1E4E8] bg-white/80 backdrop-blur-xl p-10 text-center">
        <div className="relative inline-block">
          <div className="h-20 w-20 rounded-full bg-[#43B02A]/10 grid place-items-center mx-auto">
            <Smartphone className="h-10 w-10 text-[#43B02A]" strokeWidth={1.75} />
          </div>
          <div className="absolute inset-0 rounded-full border-4 border-[#43B02A]/30 border-t-[#43B02A] animate-spin" />
        </div>
        <h2 className="font-heading font-black text-2xl mt-8">Check your phone</h2>
        <p className="text-[#57606A] mt-3 leading-relaxed">
          An M-Pesa STK Push has been sent to
          <span className="block font-mono font-bold text-[#1A1C20] mt-1">{phone}</span>
        </p>
        <div className="mt-6 border border-[#E1E4E8] bg-[#F7F7F5] p-4 font-mono text-sm">
          Enter your M-Pesa PIN to pay <span className="font-bold text-[#43B02A]">{KES(amount)}</span>
        </div>
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-[#57606A] font-mono">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />WAITING FOR CONFIRMATION...
        </div>
        <button onClick={onCancel} data-testid="cancel-btn" className="mt-8 text-xs font-mono text-[#57606A] hover:text-[#43B02A] uppercase tracking-[0.2em]">
          Cancel and start over
        </button>
      </div>
    </div>
  );
}

/* ---------------- Step 4: Connected ---------------- */
function Connected({ sub, remainingMs, onDone, receipt, onConnectAnotherDevice }) {
  const moreSlots = (sub.simultaneous_devices || 1) > 1;
  return (
    <div data-testid="step-connected" className="max-w-2xl mx-auto pt-2">
      <div className="border border-[#E1E4E8] bg-white p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-[#43B02A] grid place-items-center">
            <CheckCircle2 className="h-7 w-7 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#43B02A]">// CONNECTED</div>
            <div className="font-heading font-black text-2xl">You&apos;re online</div>
          </div>
        </div>
        <div className="mt-5 text-center py-4 border-y border-[#E1E4E8]">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#57606A]">TIME REMAINING</div>
          <div className="font-mono font-black text-5xl sm:text-6xl mt-2 tabular-nums text-[#1A1C20]">{formatCountdown(remainingMs)}</div>
          <div className="font-mono text-xs text-[#57606A] mt-2 uppercase tracking-[0.2em]">hh · mm · ss</div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-5">
          <div className="border border-[#E1E4E8] p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Package</div>
            <div className="font-heading font-bold mt-1">{sub.package_name}</div>
          </div>
          <div className="border border-[#E1E4E8] p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Speed</div>
            <div className="font-mono font-bold text-[#43B02A] mt-1">↓ {formatBandwidth(sub.download_kbps)}</div>
          </div>
        </div>
        {moreSlots && receipt && (
          <div className="mt-5 border border-[#43B02A]/30 bg-[#43B02A]/5 p-3 text-sm">
            <div className="font-bold">This plan covers {sub.simultaneous_devices} devices.</div>
            <div className="text-[#57606A] mt-1">
              Share the M-Pesa code <span className="font-mono font-bold text-[#1A1C20]">{receipt}</span> — on each other device,
              tap &quot;Use M-Pesa code&quot; to join.
            </div>
            <Button
              type="button" data-testid="connect-another-device-btn" onClick={onConnectAnotherDevice}
              variant="outline"
              className="mt-3 w-full h-10 rounded-full border-[#43B02A] text-[#43B02A] hover:bg-[#43B02A]/10 font-bold text-xs"
            >
              CONNECT ANOTHER DEVICE
            </Button>
          </div>
        )}
        <div className="mt-5 flex items-center gap-2 text-xs text-[#57606A] font-mono">
          <Clock className="h-3.5 w-3.5" />Enjoy browsing! We&apos;ll disconnect automatically when your time ends.
        </div>
        <Button data-testid="done-btn" onClick={onDone} className="mt-6 w-full h-12 rounded-full bg-[#1A1C20] hover:bg-[#333] text-white font-bold">BUY ANOTHER PACKAGE</Button>
      </div>
    </div>
  );
}

/* ---------------- Step 6: Paid, waiting for a device to claim the slot ---------------- */
function DeviceClaimPending({ sub, receipt, onConnectThisDevice, onDone }) {
  return (
    <div data-testid="step-device-claim" className="max-w-2xl mx-auto pt-2">
      <div className="border border-[#E1E4E8] bg-white p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-[#43B02A] grid place-items-center">
            <CheckCircle2 className="h-7 w-7 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#43B02A]">// PAYMENT RECEIVED</div>
            <div className="font-heading font-black text-2xl">{sub.package_name} is ready</div>
          </div>
        </div>
        <p className="mt-4 text-[#57606A]">
          This device couldn&apos;t be connected automatically. Enter this M-Pesa code below
          (or on whichever device should get online) to claim it.
        </p>
        <div className="mt-4 border border-[#E1E4E8] bg-[#F7F7F5] p-4 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">M-Pesa Receipt</div>
          <div className="font-mono font-black text-3xl tracking-widest mt-2">{receipt || "—"}</div>
          {receipt && (
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(receipt); toast.success("Copied to clipboard"); }}
              className="mt-2 text-xs font-mono uppercase tracking-[0.2em] text-[#43B02A] hover:underline"
            >
              <Copy className="inline h-3 w-3 mr-1" /> Copy code
            </button>
          )}
        </div>
        <Button data-testid="connect-this-device-btn" onClick={onConnectThisDevice}
                className="mt-4 w-full h-12 rounded-full bg-[#43B02A] hover:bg-[#368F22] text-white font-bold">
          CONNECT THIS DEVICE INSTEAD
        </Button>
        <button onClick={onDone} className="mt-3 w-full text-center text-xs font-mono uppercase tracking-[0.2em] text-[#57606A] hover:text-[#43B02A]">
          Done — I&apos;ll connect elsewhere
        </button>
      </div>
    </div>
  );
}

/* ---------------- Step 7: Paid for a different device by MAC ---------------- */
function BoughtForOtherDevice({ sub, mac, onDone, onRetryConnect }) {
  return (
    <div data-testid="step-bought-for-device" className="max-w-2xl mx-auto pt-2">
      <div className="border border-[#E1E4E8] bg-white p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-[#43B02A] grid place-items-center">
            <CheckCircle2 className="h-7 w-7 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#43B02A]">// PAYMENT RECEIVED</div>
            <div className="font-heading font-black text-2xl">{sub.package_name} is ready</div>
          </div>
        </div>
        <p className="mt-4 text-[#57606A]">
          That device is pre-authorized now — not this one. The moment it joins this WiFi
          (no portal page, no login needed), it&apos;ll have internet automatically.
        </p>
        <div className="mt-4 border border-[#E1E4E8] bg-[#F7F7F5] p-4 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Device MAC Address</div>
          <div className="font-mono font-black text-xl tracking-widest mt-2">{mac || "—"}</div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-5">
          <div className="border border-[#E1E4E8] p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Speed</div>
            <div className="font-mono font-bold text-[#43B02A] mt-1">↓ {formatBandwidth(sub.download_kbps)}</div>
          </div>
          <div className="border border-[#E1E4E8] p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Valid until</div>
            <div className="font-mono font-bold mt-1 text-sm">{new Date(sub.expires_at).toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short" })}</div>
          </div>
        </div>
        <p className="mt-5 text-xs text-[#57606A]">
          Device not online yet? Once it&apos;s joined this WiFi, retry the connection below.
        </p>
        <Button data-testid="retry-connect-btn" onClick={onRetryConnect} variant="outline"
                className="mt-2 w-full h-11 rounded-full border-[#43B02A] text-[#43B02A] hover:bg-[#43B02A]/10 font-bold">
          RETRY CONNECT
        </Button>
        <Button data-testid="done-btn" onClick={onDone} className="mt-3 w-full h-12 rounded-full bg-[#1A1C20] hover:bg-[#333] text-white font-bold">BUY ANOTHER PACKAGE</Button>
      </div>
    </div>
  );
}

/* ---------------- Step 5: Failed ---------------- */
function Failed({ onRetry }) {
  return (
    <div data-testid="step-failed" className="max-w-lg mx-auto pt-8">
      <div className="border border-[#FF3B30]/30 bg-white p-8 text-center">
        <div className="h-16 w-16 rounded-full bg-[#FF3B30]/10 grid place-items-center mx-auto">
          <XCircle className="h-9 w-9 text-[#FF3B30]" strokeWidth={2} />
        </div>
        <h2 className="font-heading font-black text-2xl mt-6">Payment failed</h2>
        <p className="text-[#57606A] mt-3">The STK Push wasn&apos;t completed. Please check your M-Pesa balance and try again.</p>
        <Button data-testid="retry-btn" onClick={onRetry} className="mt-6 w-full h-12 rounded-full bg-[#43B02A] hover:bg-[#368F22] text-white font-bold">TRY AGAIN</Button>
      </div>
    </div>
  );
}

/* ---------------- Voucher redemption dialog ---------------- */
function VoucherDialog({ open, onOpenChange, onRedeemed }) {
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!code.trim() || phone.replace(/\D/g, "").length < 9) {
      toast.error("Enter your voucher code and phone number");
      return;
    }
    setBusy(true);
    try {
      const hotspotParams = getHotspotParams();
      const payload = { code: code.trim(), phone };
      if (hotspotParams.mac) payload.mac_address = hotspotParams.mac;
      if (hotspotParams.ip) payload.ip_address = hotspotParams.ip;
      const { data } = await api.post("/portal/voucher/redeem", payload);
      toast.success(data.message);
      // Fetch the newly-created transaction to obtain the subscription payload.
      const { data: tx } = await api.get(`/portal/transaction/${data.transaction_id}`);
      if (tx.subscription) onRedeemed(tx.subscription);
      setCode(""); setPhone("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-none max-w-sm" data-testid="voucher-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Redeem voucher</DialogTitle>
          <DialogDescription>Enter your 10-character voucher code and phone number.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Voucher Code</Label>
            <Input data-testid="voucher-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                   className="mt-1 h-12 rounded-none font-mono uppercase tracking-widest" placeholder="XXXXXXXXXX" />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Your Phone</Label>
            <Input data-testid="voucher-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                   className="mt-1 h-12 rounded-none" placeholder="0712345678" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          <Button data-testid="voucher-redeem-btn" onClick={submit} disabled={busy}
                  className="rounded-none bg-[#43B02A] hover:bg-[#368F22] text-white font-bold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "REDEEM"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Quick-reconnect with an M-Pesa receipt ---------------- */
function ReceiptConnectDialog({ open, onOpenChange, onConnected, prefillReceipt, prefillMac }) {
  const [receipt, setReceipt] = useState("");
  const [macInput, setMacInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReceipt(prefillReceipt || "");
    const hotspotParams = getHotspotParams();
    setMacInput(formatMacInput(prefillMac || hotspotParams.mac || ""));
  }, [open, prefillReceipt, prefillMac]);

  const submit = async () => {
    if (!receipt.trim()) {
      toast.error("Enter your M-Pesa receipt code");
      return;
    }
    if (macInput.length !== 17) {
      toast.error("Enter the device's full MAC address");
      return;
    }
    const hotspotParams = getHotspotParams();
    // Only attach this browser's own detected IP when the MAC being
    // submitted is actually this device — never pair a stranger's IP with
    // someone else's MAC when the field was edited to target another device.
    const isThisDevice = hotspotParams.mac && macInput === formatMacInput(hotspotParams.mac);
    setBusy(true);
    try {
      const { data } = await api.post("/portal/receipt/connect", {
        receipt: receipt.trim(), mac_address: macInput, ip_address: isThisDevice ? hotspotParams.ip : null,
      });
      toast.success(data.message);
      onConnected(data.subscription);
      setReceipt(""); setMacInput("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-none max-w-sm" data-testid="receipt-connect-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Get online with an M-Pesa code</DialogTitle>
          <DialogDescription>Enter the M-Pesa code from a payment — yours, or one shared with you.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">M-Pesa Receipt</Label>
            <Input data-testid="receipt-connect-code" value={receipt} onChange={(e) => setReceipt(e.target.value.toUpperCase())}
                   className="mt-1 h-12 rounded-none font-mono uppercase tracking-widest" placeholder="e.g. MABC123XYZ" />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Device MAC Address</Label>
            <Input
              data-testid="receipt-connect-mac" value={macInput}
              onChange={(e) => setMacInput(formatMacInput(e.target.value))}
              placeholder="e.g. AA:BB:CC:DD:EE:FF"
              type="text" inputMode="text" autoComplete="off" autoCapitalize="characters"
              className="mt-1 h-12 rounded-none font-mono uppercase tracking-widest"
            />
            <p className="mt-1 text-xs text-[#57606A]">Defaults to this device — edit it to connect a different one.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          <Button data-testid="receipt-connect-btn" onClick={submit} disabled={busy}
                  className="rounded-none bg-[#43B02A] hover:bg-[#368F22] text-white font-bold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "CONNECT"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
